import { FeedCard as SharedFeedCard } from '@socialchorus/shared-ui-components';
import { JSXElementConstructor, ReactElement, useMemo } from 'react';
import { Content } from '../../../../models/content/types';
import { FeedCardActionsContainerProps, FeedCardActionsProps } from './type';
import { FeedCardActionLikeButton } from './action-like';
import { FeedCardActionComment } from './action-comment';
import { FeedCardActionBookmark } from './action-bookmark';
import { FeedCardActionShare } from './action-share';
import { FeedCardActionEdit } from './action-edit';
import { FeedCardActionDuplicate } from './action-duplicate';
import { FeedCardActionArchive } from './action-archive';
import { usePatronSelector } from '../../../../common/use-patron-selector';
import { advocateSelectors } from '../../../../models/advocate';
import { contentIsOwnedBy } from '../../../../models/content/helpers';
import { Feature, getFeatureFlag } from '../../../../models/features/features';

export function FeedCardActions({
  content,
  analyticsData,
  maxVisibleActions = 3,
  compactCounts,
  constrainWidth,
}: FeedCardActionsContainerProps) {
  const advocateId = usePatronSelector((state) =>
    advocateSelectors.getAdvocateId(state)
  );
  // UGCv2 Duplicate/Archive stay behind its flag to match content detail's info-footer's gate.
  const ugcEnabled = !!usePatronSelector((state) =>
    getFeatureFlag(state, Feature.UGC_ENABLED)
  );

  const [actions, hiddenActions] = useMemo(() => {
    const actions: ReactElement[] = [];
    const hiddenActions: ReactElement[] = [];

    const renderAction = (
      key: string,
      element: JSXElementConstructor<FeedCardActionsProps>,
      isHidden: boolean
    ) => {
      const ActionComponent = element;
      return (
        <ActionComponent
          key={key}
          content={content}
          analyticsData={analyticsData}
          compactCounts={compactCounts}
          constrainWidth={constrainWidth}
          isHidden={isHidden}
        />
      );
    };

    const entries = Object.entries(ContentCardToolbarActions);
    const context: ActionContext = { advocateId, ugcEnabled };

    // Inline actions (engagement + edit): the first maxVisibleActions render inline,
    // the rest move into the "more" menu. The last eligible inline action stays inline
    // rather than spawning a lone "more" button. Eligibility is resolved before the
    // loop so that rule reads the eligible entries rather than the declared ones, and
    // the always-overflow ones below can't shift the index math either way.
    entries
      .filter(
        ([, action]) => !action.overflow && action.canShow(content, context)
      )
      .forEach(([key, { element }], index, inlineEntries) => {
        if (
          actions.length >= maxVisibleActions &&
          !(index === inlineEntries.length - 1 && hiddenActions.length === 0)
        ) {
          hiddenActions.push(renderAction(key, element, true));
        } else {
          actions.push(renderAction(key, element, false));
        }
      });

    // Management actions (Duplicate/Archive) always live in the "more" menu, per
    // design, regardless of how many inline actions are shown.
    entries
      .filter(([, action]) => action.overflow)
      .forEach(([key, { canShow, element }]) => {
        if (canShow(content, context))
          hiddenActions.push(renderAction(key, element, true));
      });

    return [actions, hiddenActions];
  }, [
    analyticsData,
    content,
    compactCounts,
    constrainWidth,
    maxVisibleActions,
    advocateId,
    ugcEnabled,
  ]);

  return (
    <SharedFeedCard.Actions>
      <>
        {actions}
        {hiddenActions.length > 0 ? (
          <SharedFeedCard.HiddenActions>
            {hiddenActions}
          </SharedFeedCard.HiddenActions>
        ) : null}
      </>
    </SharedFeedCard.Actions>
  );
}

/**
 * Whether the content offers any action at all. Lets a caller leave the action
 * row out entirely rather than composing an empty one — which the `-minimal`
 * layouts render as a bare divider.
 */
export function useHasFeedCardActions(content: Content) {
  const advocateId = usePatronSelector((state) =>
    advocateSelectors.getAdvocateId(state)
  );
  const ugcEnabled = !!usePatronSelector((state) =>
    getFeatureFlag(state, Feature.UGC_ENABLED)
  );

  return Object.values(ContentCardToolbarActions).some(({ canShow }) =>
    canShow(content, { advocateId, ugcEnabled })
  );
}

type ActionContext = {
  advocateId?: number | null;
  ugcEnabled: boolean;
};

const ContentCardToolbarActions: {
  [key: string]: {
    canShow: (content: Content, context: ActionContext) => boolean;
    element: JSXElementConstructor<FeedCardActionsProps>;
    overflow?: boolean;
  };
} = {
  like: {
    canShow: (content) => content.publication_state === 'published',
    element: FeedCardActionLikeButton,
  },
  comment: {
    canShow: (content) =>
      content.publication_state === 'published' && content.is_commentable,
    element: FeedCardActionComment,
  },
  bookmark: {
    canShow: (content) => content.publication_state === 'published',
    element: FeedCardActionBookmark,
  },
  share: {
    canShow: (content) => content.can_share,
    element: FeedCardActionShare,
  },
  edit: {
    canShow: (content) =>
      content.is_editable &&
      content.publication_state !== 'archived' &&
      !(content.processing_state && content.processing_state !== 'completed') &&
      !(content.version === 2 && content.publication_state !== 'draft'),
    element: FeedCardActionEdit,
  },
  duplicate: {
    // Duplicate is available at any publication_state for the author's own UGC v2
    // posts; the BE copy endpoint 403s non-owners. Disabled state is handled inside
    // the component when is_duplicatable is false. Lives in the "more" menu per design.
    canShow: (content, { advocateId, ugcEnabled }) =>
      ugcEnabled &&
      content.version === 2 &&
      contentIsOwnedBy(content.created_by, advocateId),
    overflow: true,
    element: FeedCardActionDuplicate,
  },
  archive: {
    canShow: (content, { advocateId, ugcEnabled }) =>
      ugcEnabled &&
      !!content.is_archivable &&
      content.publication_state !== 'archived' &&
      content.version === 2 &&
      contentIsOwnedBy(content.created_by, advocateId),
    overflow: true,
    element: FeedCardActionArchive,
  },
};
